import { fetchGuideEmoticonList } from '@module/apis/client-side/guide';
import type { GuideEmoticonListResponse, GuideEmoticonParams } from '@module/apis/client-side/guide/type';
import { useEmoticonFilterStore } from '@module/stores/useEmoticonFilterStore';
import { type UseInfiniteQueryResult, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import type { CustomUseInfiniteQueryOptions, CustomUseQueryOptions } from '../../type';
import { guideQueryKeys } from '../guideQueryKeys';
import { useGuideEmoticonListInfiniteQuery } from './useGuideEmoticonListInfiniteQuery';
import { computeHeartRange } from './util';
export type EmoticonType = 'normal' | 'signature' | 'term';
interface UseEmoticonFilterOptions<T extends boolean> {
  isInfinite: T;
  limit?: number;
  options?: T extends true ? CustomUseInfiniteQueryOptions<GuideEmoticonListResponse, Error> : CustomUseQueryOptions<GuideEmoticonListResponse, Error>;
  type: EmoticonType;
  fixedBjId?: string;
  fixedFilterType?: 'all' | 'crew';
  userIdx?: number;
}
export function useEmoticonFilterQuery(options: UseEmoticonFilterOptions<true>): {
  queryResult: UseInfiniteQueryResult<GuideEmoticonListResponse, Error>;
};
export function useEmoticonFilterQuery(options: UseEmoticonFilterOptions<false>): {
  queryResult: UseQueryResult<GuideEmoticonListResponse, Error>;
};
export function useEmoticonFilterQuery<T extends boolean>({
  isInfinite,
  limit: defaultLimit = 20,
  options,
  type,
  fixedBjId = '',
  fixedFilterType,
  userIdx
}: UseEmoticonFilterOptions<T>) {
  const {
    bjId,
    filterType,
    heartCategory,
    minHeart,
    maxHeart,
    page
  } = useEmoticonFilterStore(state => ({
    bjId: state.bjId,
    filterType: state.filterType,
    heartCategory: state.heartCategory,
    minHeart: state.minHeart,
    maxHeart: state.maxHeart,
    page: state.page
  }), shallow);
  const offset = (page - 1) * defaultLimit;
  const apiParams = useMemo((): Omit<GuideEmoticonParams, 'offset' | 'limit'> => {
    const {
      startHeart,
      endHeart
    } = computeHeartRange({
      heartCategory,
      minHeart,
      maxHeart
    });
    const mapper = {
      normal: 'default',
      signature: 'custom',
      term: 'term'
    } as const;
    return {
      type: mapper[type],
      ...(!userIdx && {
        id: fixedBjId || bjId
      }),
      ...((fixedFilterType === 'crew' || filterType === 'crew') && {
        crewBJYN: 'Y'
      }),
      ...(startHeart && {
        startHeart
      }),
      ...(endHeart && {
        endHeart
      }),
      ...(userIdx && {
        userIdx
      })
    };
  }, [bjId, fixedBjId, type, filterType, heartCategory, minHeart, maxHeart]);

  // Narrow unified options
  const queryOptions = options as CustomUseQueryOptions<GuideEmoticonListResponse, Error> | undefined;
  const infiniteQueryOptions = options as CustomUseInfiniteQueryOptions<GuideEmoticonListResponse, Error> | undefined;
  const queryResult = useQuery({
    queryKey: guideQueryKeys.fetchGuideEmoticonList({
      ...apiParams,
      limit: defaultLimit,
      offset // 계산된 offset 사용
    }).queryKey,
    queryFn: () => fetchGuideEmoticonList({
      ...apiParams,
      limit: defaultLimit,
      offset
    }),
    ...queryOptions,
    enabled: !isInfinite && queryOptions?.enabled
  });
  const infiniteQueryResult = useGuideEmoticonListInfiniteQuery({
    ...apiParams,
    limit: defaultLimit
  }, {
    ...infiniteQueryOptions,
    enabled: isInfinite && infiniteQueryOptions?.enabled
  });
  if (isInfinite) {
    return {
      queryResult: infiniteQueryResult
    };
  }
  return {
    queryResult
  };
}